import { SignInPrompt, WordsHappenButton, WordsHappenFormAlert } from '@words-happen/ui-components';
import type React from 'react';
import { useId } from 'react';
import { MessageScreen, messageScreenActionsClassName, messageScreenStatusClassName } from '../../components';
import type { Resend } from '../use-resend';

export interface AccountExistsScreenProps {
  email: string;
  /** "Email me a sign-in link": its progress, cooldown and error (useResend). */
  signInLink: Pick<Resend, 'sending' | 'cooldown' | 'error'>;
  /** Emails a sign-in link to `email`. */
  onSendSignInLink: () => void;
  onSignIn: () => void;
  onForgotPassword: () => void;
}

/** Registering with an address that already has an account (auth/email-already-in-use). */
export const AccountExistsScreen: React.FC<AccountExistsScreenProps> = ({
  email,
  signInLink,
  onSendSignInLink,
  onSignIn,
  onForgotPassword,
}) => {
  const cooldownId = useId();

  return (
    <MessageScreen
      title="It looks like you may already have an account"
      mood="surprised"
      action={
        <div className={messageScreenActionsClassName}>
          {signInLink.error ? <WordsHappenFormAlert>{signInLink.error}</WordsHappenFormAlert> : null}
          <WordsHappenButton
            variant="coral"
            size="lg"
            loading={signInLink.sending}
            disabled={signInLink.cooldown > 0}
            aria-describedby={signInLink.cooldown > 0 ? cooldownId : undefined}
            onClick={onSendSignInLink}
          >
            Email me a sign-in link
          </WordsHappenButton>
          {signInLink.cooldown > 0 ? (
            <p id={cooldownId} className={messageScreenStatusClassName}>
              You can try again in {signInLink.cooldown}s
            </p>
          ) : null}
          <SignInPrompt prefix={null} action="Sign in with your password" onAction={onSignIn} />
          <SignInPrompt prefix={null} action="Forgot password?" onAction={onForgotPassword} />
        </div>
      }
    >
      <p>
        We can email a sign-in link to <strong>{email}</strong>.
      </p>
    </MessageScreen>
  );
};
